import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { CommonModule } from '@angular/common';
import { ModuleEntityService } from '../../module/services/module.entity.service';
import { MatTableModule } from '@angular/material/table';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, switchMap, tap } from 'rxjs';
import { ModuleEntity } from '../../module/module.entity';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { LearningUnitEntityService } from '../../learning-unit/services/learning-unit.entity.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ModuleFormDialogComponent } from './module-form-dialog/module-form-dialog.component';
import { map } from 'rxjs/operators';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';

@Component({
  selector: 'pixl-module-table',
  templateUrl: './module-table.component.html',
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginator,
    MatButton,
    MatIcon,
    MatIconButton,
  ],
  styleUrls: ['./module-table.component.css']
})

export class ModuleTableComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private moduleService = inject(ModuleEntityService);
  private learningUnitService = inject(LearningUnitEntityService);
  private dialog = inject(MatDialog);
  private refresh$ = new BehaviorSubject<void>(undefined);

  modules$!: Observable<ModuleEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  displayedColumns: string[] = ['actions', 'id', 'preTitle', 'title', 'description', 'status', 'learningUnits'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 5;
  pageIndex = 0;
  totalItems = 1000;

  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: 0, pageSize: 5 });

  ngOnInit() {
    this.modules$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query = {
          'include': 'learningUnits',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
        };

        // Wrap ensureLoaded (Promise<void>) in from()
        return from(this.moduleService.ensureLoaded(query)).pipe(
          // select returns Observable<ModuleEntity[]>
          switchMap(() => this.store.select(this.moduleService.allByQuery(query))),
          // filter out deleted IDs
          map((modules: ModuleEntity[]) => modules.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            // Use the concrete ModuleService
            const state = this.store.selectSnapshot(this.moduleService.stateClass) as EntityStateModel<ModuleEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  addModule(): void {
    const dialogRef = this.dialog.open(ModuleFormDialogComponent, {
      width: '500px',
      data: {} // optional: pass defaults here
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ModuleEntity> | undefined) => {
      if (result) {
        const newModuleInstance = Object.assign(new ModuleEntity(), result);
        await this.moduleService.create(newModuleInstance);

        // 🔄 Trigger reload
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();
      }
    });
  }

  editModule(module: ModuleEntity) {
    const dialogRef = this.dialog.open(ModuleFormDialogComponent, {
      width: '500px',
      data: { ...module } // pass existing module data
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ModuleEntity> | undefined) => {
      if (!result) return;

      // merge the changes into the existing module
      const updatedModule = Object.assign(new ModuleEntity(), module, result);

      // call your existing update logic
      await firstValueFrom(this.moduleService.update(updatedModule));

      // trigger refresh so table updates
      this.refresh$.next();
    });
  }

  async deleteModule(module: ModuleEntity) {
    const confirmed = window.confirm(`Do you really want to delete module "${module.title}"?`);
    if (!confirmed) return;

    if (!module?.id) {
      console.error('Module ID is undefined!', module);
      return;
    }

    await firstValueFrom(this.moduleService.delete(module.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();
  }

  getLearningUnitNames(learningUnits: { id: string }[]): string {
    if (!learningUnits?.length) return '---';

    return learningUnits
      .map(lu => {
        const entity = this.learningUnitService.stateClass.byId(
          this.store.selectSnapshot(this.learningUnitService.stateClass)
        )(lu.id);
        return entity?.title || lu.id;
      })
      .join(', ');
  }

  trackById(index: number, module: ModuleEntity): string {
    return module.id;
  }
}
